/*
<summary>
   This functional component is all about rendering the Add/Edit Tool Management multi-step form (Step 1 tool details, Step 2 crimp/user-created inspection) with step navigation and submit-time validation.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { ExclamationCircleFill } from 'react-bootstrap-icons';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { IToolManagementVM } from '../../../models/response/IToolManagementResponse';
import { IAddEditFormProps } from '../../../models/forms/IForm';
import { IOption } from '../../../models/ICommon';
import InspectionFormatEnum from '../../../constants/enums/inspection-format-enum';
import InspectionCycleEnum from '../../../constants/enums/inspection-cycle-enum';
import { ToolManagementValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import LoaderButton from '../../../shared-components/button/loader-button';
import DefaultModal from '../../../shared-components/modal/default-modal';
import { formatMessage } from '../../../translations/format-message';
import ToolStepper from './steps/tool-stepper';
import StepOneToolDetails from './steps/step-one-tool-details';
import StepTwoCrimpInspection from './steps/step-two-crimp-inspection';
import StepTwoUserCreatedInspection from './steps/step-two-user-created-inspection';
import {
  validateCrimpInspectionStep,
  validateUserCreatedInspectionStep,
} from './steps/step-two-validation';

const showAttentionToast = (message: string) =>
  toast(message, { icon: <ExclamationCircleFill color="#ffcc00" size={18} /> });

// InspectionFormat is a fixed enum now (tool_master.inspection_format is an integer column).
// The native <select> it's bound to always yields a string via Formik state (e.g. "0"), so the
// value is coerced to a number here before comparing against the enum.
const isUserCreatedInspectionFormat = (value: number | string) =>
  Number(value) === InspectionFormatEnum.UserCreated;
const isCrimpToolInspectionFormat = (value: number | string) =>
  Number(value) === InspectionFormatEnum.CrimpingToolInspection;
const isNoneInspectionCycle = (value: number | string) =>
  Number(value) === InspectionCycleEnum.None;

interface IProps extends IAddEditFormProps<IToolManagementVM> {
  factoryOptions: IOption[];
  processOptions: IOption[];
}

/**
* This functional component renders the Add/Edit Tool Management multi-step form, handling
* step navigation between Step 1 (tool details) and Step 2 (crimp/user-created inspection)
* along with submit-time validation.
* @param props: Tool identifier, initial values, options and submit/close handlers
*/
const AddEditToolManagementForm = ({
  id,
  initialValues,
  isLoading,
  factoryOptions,
  processOptions,
  submitHandler,
  onModalClosed,
}: IProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  // This flag briefly blocks the Save/Next button after a step change so a fast double-click can't fire an unintended submit.
  const [isTransitioning, setIsTransitioning] = useState(false);
  // This flag is used to only show Step 2's per-tab validation errors once a submit has actually been attempted.
  const [step2SubmitAttempted, setStep2SubmitAttempted] = useState(false);
  // This flag does the same thing as step2SubmitAttempted, but for the Crimp Inspection rows table.
  const [crimpSubmitAttempted, setCrimpSubmitAttempted] = useState(false);
  // This ref is set only by the Save/Update button's onClick before the native submit event runs, so onSubmit ignores any other trigger (e.g. implicit Enter-key submission).
  const submitTriggeredRef = useRef(false);

  /**
   * This useEffect is used to reset the Step 2 submit-attempted flags whenever the form goes back to Step 1, and to briefly hold the transitioning flag across a step change.
   */
  useEffect(() => {
    setIsTransitioning(true);
    if (step === 1) {
      setStep2SubmitAttempted(false);
      setCrimpSubmitAttempted(false);
    }
    const timer = setTimeout(() => setIsTransitioning(false), 400);
    return () => clearTimeout(timer);
  }, [step]);

  return (
    <DefaultModal
      id={id}
      size="xl"
      className="tool-management-modal"
      addHeader="add_tool_management"
      updateHeader="update_tool_management"
      isLoading={isLoading}
      onModalClosed={onModalClosed}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={ToolManagementValidateSchema}
        validateOnBlur={false}
        onSubmit={async (values) => {
          if (!submitTriggeredRef.current) return;
          submitTriggeredRef.current = false;

          const skipsStepTwo = isNoneInspectionCycle(values.InspectionCycle);
          if (step === 1 && !skipsStepTwo) return;

          if (!skipsStepTwo && isUserCreatedInspectionFormat(values.InspectionFormat)) {
            const result = validateUserCreatedInspectionStep(
              values.InspectionTabs || []
            );
            if (!result.isValid) {
              setStep2SubmitAttempted(true);
              if (result.isWarning) {
                showAttentionToast(formatMessage(result.messageKey as string));
              } else {
                toast.error(formatMessage(result.messageKey as string));
              }
              return;
            }
          }

          if (!skipsStepTwo && isCrimpToolInspectionFormat(values.InspectionFormat)) {
            const result = validateCrimpInspectionStep(values.CrimpRows || []);
            if (!result.isValid) {
              // Errors are already shown inline next to each row (or below
              // the table for "no rows"), so skip the duplicate toast here.
              setCrimpSubmitAttempted(true);
              return;
            }
          }

          const cleanValues = {
            ...values,
            InspectionTabs: (values.InspectionTabs || []).map((t) => ({
              ...t,
              CheckItems: (t.CheckItems || []).filter(
                (ci) => (ci.CheckItemMessage || '').trim() !== ''
              ),
            })),
          };

          submitHandler(cleanValues);
        }}
      >
        {({ values, errors, validateForm, setTouched, submitForm }) => {
          const skipsStepTwo = isNoneInspectionCycle(values.InspectionCycle);

          /**
           * This function is used to validate Step 1 and move to Step 2 when there are no
           * errors - except for Inspection Cycle "None" tools, which have no Step 2 to move to,
           * so a valid Step 1 is submitted directly instead. Either way, invalid fields are
           * marked as touched so their errors show.
           */
          const handleNext = async () => {
            if (isTransitioning) return;
            const formErrors = await validateForm();
            if (Object.keys(formErrors).length === 0) {
              if (skipsStepTwo) {
                submitTriggeredRef.current = true;
                submitForm();
              } else {
                setStep2SubmitAttempted(false);
                setStep(2);
              }
            } else {
              setTouched(
                Object.keys(formErrors).reduce(
                  (acc, key) => ({ ...acc, [key]: true }),
                  {}
                )
              );
            }
          };

          return (
            <Form className="tool-management-form">
              {isLoading && <FormLoader loading={isLoading} contained />}
              {/* A tool with Inspection Cycle "None" only ever has one step (it saves directly
                  from Step 1), so a stepper showing a single "Step 1" circle has nothing left
                  to indicate progress toward - skip rendering it entirely. */}
              {!skipsStepTwo && <ToolStepper step={step} />}
              <Modal.Body className="overflow-y-auto">
                {step === 1 ? (
                  <StepOneToolDetails
                    factoryOptions={factoryOptions}
                    processOptions={processOptions}
                    isAddMode={id < 0}
                  />
                ) : isCrimpToolInspectionFormat(values.InspectionFormat) ? (
                  <StepTwoCrimpInspection submitAttempted={crimpSubmitAttempted} />
                ) : (
                  <StepTwoUserCreatedInspection submitAttempted={step2SubmitAttempted} />
                )}
              </Modal.Body>
              <Modal.Footer>
                <div className="d-flex justify-content-center">
                  <div className="row tab-action g-0">
                    <div className="col-12">
                      {step === 1 ? (
                        <>
                          <Button
                            variant="light-danger"
                            type="button"
                            onClick={onModalClosed}
                            disabled={isLoading}
                          >
                            <FormattedMessage id="cancel" />
                          </Button>
                          <LoaderButton
                            type="button"
                            onClick={handleNext}
                            isLoading={skipsStepTwo ? isLoading : false}
                            disabled={isTransitioning || (skipsStepTwo && isLoading)}
                            text={skipsStepTwo ? (id < 0 ? 'save' : 'update') : 'next'}
                          />
                        </>
                      ) : (
                        <>
                          <Button
                            variant="light-danger"
                            type="button"
                            onClick={() => setStep(1)}
                            disabled={isLoading || isTransitioning}
                          >
                            <FormattedMessage id="back" />
                          </Button>
                          <LoaderButton
                            type="submit"
                            onClick={() => {
                              submitTriggeredRef.current = true;
                            }}
                            isLoading={isLoading}
                            disabled={isTransitioning}
                            text={id < 0 ? 'save' : 'update'}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </DefaultModal>
  );
};

export default AddEditToolManagementForm;
